/**
 * The plan form's awkward RouterOS bits, kept out of the component and tested
 * on their own.
 *
 * Every function here exists because a field was asking the operator to speak
 * RouterOS. Ray, 2026-08-31: *"why not input separate for ex. up and down and
 * the threshold and burse avoiding adding 'M' it should automatic so they only
 * need to input the value."*
 */

/** Megabits, as the operator types them, → RouterOS's "50M/50M". */
export function toRateLimit(up: string, down: string): string {
   const u = cleanNumber(up);
   const d = cleanNumber(down);
   if (!u || !d) return '';
   return `${u}M/${d}M`;
}

/**
 * "50M/50M" → { up: '50', down: '50' }.
 *
 * Tolerant on the way IN because a plan may have been saved before the split
 * fields existed, or read off a router where somebody typed "50000k". Anything
 * it cannot read comes back empty rather than as a wrong number.
 */
export function fromRateLimit(v: string): { up: string; down: string } {
   const m = /^\s*(\d+(?:\.\d+)?)\s*([kKmMgG]?)\s*\/\s*(\d+(?:\.\d+)?)\s*([kKmMgG]?)/.exec(
      v ?? ''
   );
   if (!m) return { up: '', down: '' };
   return {
      up: String(toMbps(m[1], m[2])),
      down: String(toMbps(m[3], m[4])),
   };
}

/** RouterOS accepts k/M/G. Everything is shown to the operator in Mbps. */
function toMbps(n: string, unit: string): number {
   const v = Number(n);
   if (!Number.isFinite(v)) return 0;
   const u = unit.toLowerCase();
   if (u === 'k') return round(v / 1000);
   if (u === 'g') return round(v * 1000);
   return round(v);
}

const round = (v: number) => Math.round(v * 100) / 100;

/**
 * Digits and one decimal point. Nothing else reaches the field.
 *
 * The old inputs took "50/50" and "40M/40M" as free text, so a plan could be
 * saved with a rate RouterOS silently ignores.
 */
export function cleanNumber(v: string): string {
   const only = (v ?? '').replace(/[^\d.]/g, '');
   const [head, ...rest] = only.split('.');
   return rest.length ? `${head}.${rest.join('')}` : head;
}

/**
 * A plan name → the /ppp/profile name SmartFi will create.
 *
 * Ray, 2026-08-31: *"routerOS profile not sure what to select. if you know the
 * right selection please default it."* He is right that it was an unanswerable
 * question — SmartFi is the thing that creates the profile, so it should name
 * it, and the operator only overrides to adopt one they already have.
 *
 * Prefixed so it is obvious on the router which profiles SmartFi manages, and
 * so it can never collide with one the operator made by hand.
 */
export function profileNameFor(planName: string): string {
   const slug = (planName ?? '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 24);
   return slug ? `smartfi-${slug}` : '';
}

/**
 * Categories the operator has already used, plus whatever they are adding now.
 *
 * Ray: *"category is selectable not a string. they can add category on the fly."*
 * A free-text category gives you "Residential", "residential" and "Resi" in one
 * price list, and no way to group by it later.
 */
export function categoryOptions(
   existing: string[],
   current: string
): string[] {
   const seen = new Map<string, string>();
   for (const c of [...DEFAULT_CATEGORIES, ...existing, current]) {
      const t = (c ?? '').trim();
      if (t) seen.set(t.toLowerCase(), t);
   }
   return [...seen.values()].sort((a, b) => a.localeCompare(b));
}

/** Seeded so the very first plan has something to pick. */
export const DEFAULT_CATEGORIES = ['Residential', 'Business', 'Promo'];
